/**
 * Notification text templates in multiple languages
 * Each notification type has templates for 'en', 'fr', and 'ar'.
 * Templates can be strings or functions that accept a payload object.
 * @example
 * notificationTexts.INVOICE_CREATED.en({ params: { invoiceNumber: 'INV-001', patient: { firstName: 'John', lastName: 'Doe' }, creator: { firstName: 'Dr', lastName: 'Smith', role: 'doctor' } } })
 * @returns {string} Invoice INV-001 created for John Doe by Dr. Smith
 */

import formatDate from "./formatDate";
import textTranslator from "./useTextTranslator";

export const notificationTexts = {
  /**
   * patient notifications
   * @param {object} params - { patient, creator }
   * @example notificationTexts.PATIENT_CREATED.en({ params: { patient: { firstName: 'John', lastName: 'Doe' }, creator: { firstName: 'Dr', lastName: 'Smith', role: 'doctor' } } })
   * @returns {string} New patient John Doe has been added by Dr. Smith
   */
  PATIENT_CREATED: {
    en: ({ params }) =>
      `New patient ${params.patientName} has been added${
        params.createdByName ? ` by ${params.createdByName}` : ""
      }`,
    fr: ({ params }) =>
      `Nouveau patient ${params.patientName} ajouté${
        params.createdByName ? ` par ${params.createdByName}` : ""
      }`,
    ar: ({ params }) =>
      `تمت إضافة مريض جديد ${params.patientName}${
        params.createdByName ? ` بواسطة ${params.createdByName}` : ""
      }`,
  },
  PATIENT_UPDATED: {
    en: ({ params,entityId }) =>
      `Patient (#${entityId}) ${params.patientName} information updated${
        params.updatedByName ? ` by ${params.updatedByName}` : ""
      }`,
    fr: ({ params,entityId }) =>
      `Informations du patient (#${entityId}) ${params.patientName} mises à jour${
        params.updatedByName ? ` par ${params.updatedByName}` : ""
      }`,
    ar: ({ params,entityId }) =>
      `تم تحديث معلومات المريض (#${entityId}) ${params.patientName}${
        params.updatedByName ? ` بواسطة ${params.updatedByName}` : ""
      }`,
  },
  PATIENT_DELETED: {
    en: ({ params,entityId }) =>
      `${params.deletedByName} deleted the patient (#${entityId}) ${params.patientName} on ${formatDate(params.date, true)}.`,
    fr: ({ params,entityId }) =>
      `${params.deletedByName} a supprimé un patient (#${entityId}) ${params.patientName} le ${formatDate(params.date, true)}.`,
    ar: ({ params,entityId }) =>
      `قام ${params.deletedByName} بحذف مريض (#${entityId}) ${params.patientName} بتاريخ ${formatDate(params.date, true)}.`,
  },

  /**
   * appointment notifications
   * @param {object} patient - patient data
   * @param {string} date - appointment date
   * @example notificationTexts.APPOINTMENT_CREATED.en({ patient: 'John Doe', date: '2023-10-01' })
   * @returns {string} Appointment scheduled for John Doe on 2023-10-01
   * @default {en} Appointment scheduled for {patientName} on {date}
   */
  APPOINTMENT_CREATED: {
    en: ({ params, entityId }) =>
      `Appointment (#${entityId}) scheduled for ${params.patientName} (#${params.patientId}) on ${formatDate(params.date, true)}${
        params.createdByName ? ` by ${params.createdByName}` : ""
      }`,
    fr: ({ params, entityId }) =>
      `Rendez-vous (#${entityId}) programmé pour ${params.patientName} (#${params.patientId}) le ${formatDate(params.date, true)}${
        params.createdByName ? ` par ${params.createdByName}` : ""
      }`,
    ar: ({ params, entityId }) =>
      `تم تحديد موعد (#${entityId}) لـ ${params.patientName} (#${params.patientId}) بتاريخ ${formatDate(params.date, true)}${
        params.createdByName ? ` بواسطة ${params.createdByName}` : ""
      }`,
  },
  APPOINTMENT_UPDATED: {
    en: ({ params ,entityId}) =>
      `Appointment (#${entityId}) updated for ${params.patientName} (#${params.patientId})${
        params.updatedByName ? ` by ${params.updatedByName}` : ""
      }`,
    fr: ({ params ,entityId}) =>
      `Rendez-vous (#${entityId}) mis à jour pour ${params.patientName} (#${params.patientId})${
        params.updatedByName ? ` par ${params.updatedByName}` : ""
      }`,
    ar: ({ params ,entityId}) =>
      `تم تحديث موعد (#${entityId}) ${params.patientName} (#${params.patientId})${
        params.updatedByName ? ` بواسطة ${params.updatedByName}` : ""
      }`,
  },
  APPOINTMENT_DELETED: {
    en: ({ params,entityId }) =>
      `${params.deletedByName} deleted an appointment (#${entityId}) for ${params.patientName} (#${params.patientId}) on ${formatDate(params.date, true)}.`,
    fr: ({ params,entityId }) =>
      `${params.deletedByName} a annulé un rendez-vous (#${entityId}) pour ${params.patientName} (#${params.patientId}) le ${formatDate(params.date, true)}.`,
    ar: ({ params,entityId }) =>
      `قام ${params.deletedByName} بحذف موعد (#${entityId}) لـ ${params.patientName} (#${params.patientId}) بتاريخ ${formatDate(params.date, true)}.`,
  },

  /**
   * waiting room notifications
   * @param {object} patient - patient data
   * @example notificationTexts.WAITINGROOM_CREATED.en({ patient: 'John Doe' })
   * @returns {string} John Doe entered the waiting room
   * @default {en} {patientName} entered the waiting room
   */

  WAITINGROOM_CREATED: {
    en: ({ params,entityId }) =>
      `${params.patientName} (#${params.patientId}) entered the waiting room (#${entityId})${
        params.createdByName ? ` (by ${params.createdByName})` : ""
      }`,
    fr: ({ params,entityId }) =>
      `${params.patientName} (#${params.patientId}) est entré en salle d'attente (#${entityId})${
        params.createdByName ? ` (par ${params.createdByName})` : ""
      }`,
    ar: ({ params,entityId }) =>
      `دخل ${params.patientName} (#${params.patientId}) غرفة الانتظار (#${entityId})${
        params.createdByName ? ` (بواسطة ${params.createdByName})` : ""
      }`,
  },
  WAITINGROOM_UPDATED: {
    en: ({ params }) =>
      `Waiting room status updated for ${params.patientName} (#${params.patientId})${
        params.updatedByName ? ` by ${params.updatedByName}` : ""
      }`,
    fr: ({ params }) =>
      `Statut de la salle d'attente mis à jour pour ${params.patientName} (#${params.patientId})${
        params.updatedByName ? ` par ${params.updatedByName}` : ""
      }`,
    ar: ({ params }) =>
      `تم تحديث حالة قاعة الانتظار لـ ${params.patientName} (#${params.patientId})${
        params.updatedByName ? ` بواسطة ${params.updatedByName}` : ""
      }`,
  },
  WAITINGROOM_DELETED: {
    en: ({ params, entityId }) =>
      `${params.deletedByName} deleted a waiting room entry (#${entityId}) for ${params.patientName} (#${params.patientId}) on ${formatDate(params.date, true)}.`,
    fr: ({ params, entityId }) =>
      `${params.deletedByName} a supprimé une entrée de la salle d'attente (#${entityId}) pour ${params.patientName} (#${params.patientId}) le ${formatDate(params.date, true)}.`,
    ar: ({ params, entityId }) =>
      `قام ${params.deletedByName} بحذف إدخال في قاعة الانتظار (#${entityId}) لـ ${params.patientName} (#${params.patientId}) بتاريخ ${formatDate(params.date, true)}.`,
  },

  /**
   * payment notifications
   * @param {string} amount - payment amount
   * @param {object} patient - patient data
   * @example notificationTexts.PAYMENT_CREATED.en({ amount: '$100', patientName: 'John Doe' })
   * @returns {string} Payment of $100 received from John Doe
   * @default {en} Payment of {amount} received from {patientName}
   */
  PAYMENT_CREATED: {
    en: ({ params }) =>
      `New payment recorded. for ${params.patientName} (#${params.patientId}) Amount: ${params.amount} Visit ID: ${params.visitId} by ${params.createdByName}`,
    fr: ({ params }) =>
      `Nouveau paiement enregistré. pour ${params.patientName} (#${params.patientId}) Montant: ${params.amount} ID de visite: ${params.visitId} par ${params.createdByName}`,
    ar: ({ params }) =>
      `تم تسجيل دفعة جديدة. لـ ${params.patientName} (#${params.patientId}) المبلغ: ${params.amount} معرف الزيارة: ${params.visitId} بواسطة ${params.createdByName}`,
  },
  PAYMENT_UPDATED: {
    en: ({ params,entityId }) =>
      `Payment (#${entityId})  updated for ${params.patientName} (#${params.patientId}) ${
        params.newData
          ? `New amount: ${params.newData.amount} New status: ${textTranslator(params.newData.paymentStatus,"PAYMENTS")} New method: ${textTranslator(params.newData.method,"PAYMENTS")} New payment date: ${formatDate(params.newData.paymentDate)}`
          : ""
      },by ${params.updatedByName}`,
    fr: ({ params,entityId }) =>
      `Paiement (#${entityId}) mis à jour pour ${params.patientName} (#${params.patientId}) ${
        params.newData
          ? `Nouveau montant: ${params.newData.amount} Nouveau statut: ${textTranslator(params.newData.paymentStatus,"PAYMENTS")} Nouvelle méthode: ${textTranslator(params.newData.method,"PAYMENTS")} Nouvelle date de paiement: ${formatDate(params.newData.paymentDate)}`
          : ""
      },par ${params.updatedByName}`,
    ar: ({ params ,entityId }) =>
      `تم تحديث الدفع (#${entityId}) لـ ${params.patientName} (#${params.patientId}) ${
        params.newData
          ? `المبلغ الجديد: ${params.newData.amount} الحالة الجديدة: ${textTranslator(params.newData.paymentStatus,"PAYMENTS")} الطريقة الجديدة: ${textTranslator(params.newData.method,"PAYMENTS")} تاريخ الدفع الجديد: ${formatDate(params.newData.paymentDate)}`
          : ""
      },بواسطة ${params.updatedByName}`,
  },
  PAYMENT_DELETED: {
    en: ({ params, entityId }) =>
      `${params.deletedByName} deleted a payment (#${entityId}) of ${params.amount} for ${params.patientName} (#${params.patientId}) on ${formatDate(params.date, true)}.`,
    fr: ({ params, entityId }) =>
      `${params.deletedByName} a supprimé un paiement (#${entityId}) de ${params.amount} pour ${params.patientName} (#${params.patientId}) le ${formatDate(params.date, true)}.`,
    ar: ({ params, entityId }) =>
      `قام ${params.deletedByName} بحذف دفعة (#${entityId}) بمبلغ ${params.amount} للمريض ${params.patientName} (#${params.patientId}) بتاريخ ${formatDate(params.date, true)}.`,
  },

  /**
   * invoice notifications
   * @param {string} invoiceNumber - invoice number
   * @param {object} patient - patient data
   * @param {object} creator - creator data
   * @example notificationTexts.INVOICE_CREATED.en({ invoiceNumber: 'INV-001', patientName: 'John Doe' })
   * @returns {string} Invoice INV-001 created for John Doe
   * @default {en} Invoice {invoiceNumber} created for {patientName}
   */
  INVOICE_CREATED: {
    en: ({ params, entityId }) =>
      `Invoice (#${entityId}) ${params.invoiceNumber} created for ${params.patientName} (#${params.patientId}) by ${params.createdByName}`,
    fr: ({ params, entityId }) =>
      `Facture (#${entityId}) ${params.invoiceNumber} créée pour ${params.patientName} ${params.patient.lastName} (#${params.patientId}) par ${params.createdByName}`,
    ar: ({ params, entityId }) =>
      `تم إنشاء الفاتورة (#${entityId}) ${params.invoiceNumber} للمريض ${params.patientName} ${params.patient.lastName} (#${params.patientId}) بواسطة ${params.createdByName}`,
  },
  INVOICE_UPDATED: {
    en: ({ params ,entityId}) =>
      `Invoice (#${entityId}) ${params.invoiceNumber} updated for ${params.patientName} (#${params.patientId}) by ${params.updatedByName}`,
    fr: ({ params, entityId }) =>
      `Facture (#${entityId}) ${params.invoiceNumber} mise à jour pour ${params.patientName} ${params.patient.lastName} (#${params.patientId}) par ${params.updatedByName}`,
    ar: ({ params, entityId }) =>
      `تم تحديث الفاتورة (#${entityId}) ${params.invoiceNumber} للمريض ${params.patientName} ${params.patient.lastName} (#${params.patientId}) بواسطة ${params.updatedByName}`,
  },
  INVOICE_DELETED: {
    en: ({ params, entityId }) =>
      `${params.deletedByName} deleted invoice (#${entityId}) ${params.invoiceNumber} for ${params.patientName} (#${params.patientId}) on ${formatDate(params.date, true)}.`,
    fr: ({ params, entityId }) =>
      `${params.deletedByName} a supprimé la facture (#${entityId}) ${params.invoiceNumber} pour ${params.patientName} (#${params.patientId}) le ${formatDate(params.date, true)}.`,
    ar: ({ params, entityId }) =>
      `قام ${params.deletedByName} بحذف الفاتورة (#${entityId}) ${params.invoiceNumber} للمريض ${params.patientName} (#${params.patientId}) بتاريخ ${formatDate(params.date, true)}.`,
  },

  /**
   * visit notifications
   * @param {object} patient - patient data
   * @example notificationTexts.VISIT_CREATED.en({ patient: 'John Doe' })
   * @returns {string} Visit started for John Doe
   * @default {en} Visit started for {patientName}
   */
  VISIT_CREATED: {
    en: ({ params, entityId }) =>
      `Visit (#${entityId}) started for ${params.patientName} (#${params.patientId}) by ${params.createdByName}`,
    fr: ({ params, entityId }) =>
      `Visite (#${entityId}) commencée pour ${params.patientName} (#${params.patientId}) par ${params.createdByName}`,
    ar: ({ params, entityId }) =>
      `تم بدء الزيارة (#${entityId}) لـ ${params.patientName} (#${params.patientId}) بواسطة ${params.createdByName}`,
  },
  VISIT_UPDATED: {
    en: ({ params, entityId }) =>
      `Visit (#${entityId}) updated for ${params.patientName} (#${params.patientId}) by ${params.updatedByName}`,
    fr: ({ params, entityId }) =>
      `Visite (#${entityId}) mise à jour pour ${params.patientName} (#${params.patientId}) par ${params.updatedByName}`,
    ar: ({ params, entityId }) =>
      `تم تحديث الزيارة (#${entityId}) لـ ${params.patientName} (#${params.patientId}) بواسطة ${params.updatedByName}`,
  },
   
  VISIT_DELETED: {
    en: ({ params, entityId }) =>
      `${params.deletedByName} deleted a visit (#${entityId}) for ${params.patientName} (#${params.patientId}) on ${formatDate(params.date, true)}.`,
    fr: ({ params, entityId }) =>
      `${params.deletedByName} a supprimé une visite (#${entityId}) pour ${params.patientName} (#${params.patientId}) le ${formatDate(params.date, true)}.`,
    ar: ({ params, entityId }) =>
      `قام ${params.deletedByName} بحذف زيارة (#${entityId}) لـ ${params.patientName} (#${params.patientId}) بتاريخ ${formatDate(params.date, true)}.`,
  },

  /**
   * staff permissions updated notification
   * @param {string} memberName - staff member's name
   * @param {object} creator - creator data (firstName, lastName, role)
   * @example notificationTexts.USER_PERMISSIONS_UPDATED.en({ memberName: 'Jane Smith', creator: { firstName: 'Dr', lastName: 'Brown', role: 'doctor' } })
   * @returns {string} Hi Jane Smith, your permissions have been updated by Dr. Brown
   * @default {en} Hi {memberName}, your permissions have been updated by the creator
   */
  USER_PERMISSIONS_UPDATED: {
    en: ({ params, entityId }) =>
      `Hi ${params.memberName}, your permissions have been updated by ${params.updatedByName}`,
    fr: ({ params, entityId }) =>
      `Bonjour ${params.memberName}, vos autorisations ont été mises à jour par ${params.updatedByName}`,
    ar: ({ params, entityId }) =>
      `مرحبًا ${params.memberName}، تم تحديث أذوناتك بواسطة ${params.updatedByName}`,
  },
};
